/**
 * ShareToClubDialog — pick one of the user's clubs to share something with.
 *
 * Self-contained club picker (loads getMyClubs on open). The caller decides what "share" means via
 * onPick(clubId, club): a FIT workout → contributeFile, a prediction → shareClubPrediction, etc.
 */
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  List, ListItemButton, ListItemIcon, ListItemText, Box, Typography, CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { useTranslation } from 'react-i18next';
import { getMyClubs } from '../../api/clubApi';

export default function ShareToClubDialog({ open, onClose, onPick, title, subtitle, busy = false }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getMyClubs()
      .then((data) => setClubs(data || []))
      .catch(() => setClubs([]))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title || t('shareToClub', 'Share to Club')}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {subtitle && (
          <Box px={2} pt={1}>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          </Box>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        ) : clubs.length === 0 ? (
          <Box p={2}>
            <Typography variant="body2" color="text.secondary">
              {t('noClubsYet', "You're not a member of any club yet.")}
            </Typography>
          </Box>
        ) : (
          <List dense>
            {clubs.map((club) => (
              <ListItemButton key={club.id} disabled={busy} onClick={() => onPick(club.id, club)}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: club.color || colors.blueAccent[400] }} />
                </ListItemIcon>
                <ListItemText
                  primary={club.name}
                  secondary={`${club.member_count} member${club.member_count !== 1 ? 's' : ''}`}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('cancel', 'Cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}
